import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { first } from 'rxjs/operators';

import { TipoAtendimentoEnum } from 'src/app/shared/enums/tipo-atendimento.enum';
import { ApiResponse } from 'src/app/shared/interfaces/api-response.interface';
import { ContaPagamentoComandaResponse } from 'src/app/shared/models/conta-pagamento-comanda.model';
import { FormaPgtoResponse } from 'src/app/shared/models/forma-pgto.model';
import { MesasDisponiveisModel } from 'src/app/shared/models/mesas-disponiveis.model';
import { MesasOcupadasModel } from 'src/app/shared/models/mesas-ocupadas.model';
import { PagarContaRequest, PedidoBalcaoResponse, PedidoDeliveryResponse, PedidoModel, PedidoResponse, PgtoPedidoResponse, RejeitarPedidoRequest, TransferenciaMesaRequest } from 'src/app/shared/models/pedido.model';
import { VendasDetalheModel } from 'src/app/shared/models/vendas-detalhe.model';
import { AuthenticationService } from './authentication.service';
import { Mesa } from 'src/app/shared/models/mesa';
import { ConfigPdvDeliveryResponse, ConfigPedidoRequest, ConfigPedidoResponse } from 'src/app/shared/models/configuracao.model';

@Injectable({
  providedIn: "root"
})
export class PedidoService {
  constructor(
    private http: HttpClient,
    private authService: AuthenticationService
  ) { }

  private get base() {
    return `${this.authService.baseUrl}/pedido`;
  }

  public aceitarPedido(numeroComanda: number) {
    return this.http.put<ApiResponse>(`${this.base}/aceitarPedido/${numeroComanda}`, {})
      .pipe(
        first()
      );
  }

  
  public rejeitarPedido(request: RejeitarPedidoRequest) {
    return this.http.post<ApiResponse>(`${this.base}/rejeitarPedido`, request)
      .pipe(
        first()
      );
  }

  public updateCodigoStatusCab(numeroComanda: number, codigoStatus: number) {
    return this.http.post<ApiResponse<string>>(`${this.base}/atualizarCodigoStatusVendasCab?numeroComanda=${numeroComanda}&codigoStatus=${codigoStatus}`, {})
      .pipe(
        first()
      );
  }

  public cancelarPedido(numeroComanda: number) {
    return this.http.post<ApiResponse<string>>(`${this.base}/cancelarPedido?numeroComanda=${numeroComanda}`, {})
      .pipe(
        first()
      );
  }

  public cancelarVendasDet(codigoDet: number) {
    return this.http.post<ApiResponse<string>>(`${this.base}/cancelarVendasDet?codigoDet=${codigoDet}`, {})
      .pipe(
        first()
      );
  }

  public enviarPedido(pedido: PedidoModel, tipoAtendimento: TipoAtendimentoEnum) {
    return this.http.post<ApiResponse<VendasDetalheModel[]>>(`${this.base}/enviarPedido?tipoAtendimento=${tipoAtendimento}`, pedido)
      .pipe(
        first()
      );
  }

  public excluirPgto(codigoVzc: number) {
    return this.http.post<ApiResponse<boolean>>(`${this.base}/excluirPgto?codigoVzc=${codigoVzc}`, null)
      .pipe(
        first()
      );
  }

  public fecharConta(pedido: PedidoModel) {
    return this.http.post<ApiResponse<number>>(`${this.base}/fecharConta`, pedido)
      .pipe(
        first()
      );
  }

  public getFormasPgto() {
    return this.http.get<ApiResponse<FormaPgtoResponse[]>>(`${this.base}/formasPgto`)
      .pipe(
        first()
      );
  }

  public getPedidosFila() {
    return this.http.get<ApiResponse<PedidoResponse[]>>(`${this.base}/pedidosFila`)
      .pipe(
        first()
      );
  }

  public getPedidosImpressos() {
    return this.http.get<ApiResponse<PedidoResponse[]>>(`${this.base}/pedidosImpressos`)
      .pipe(
        first()
      );
  }

  public getPgtos(numeroComanda: number) {
    return this.http.get<ApiResponse<PgtoPedidoResponse[]>>(`${this.base}/obterPgtos?numeroComanda=${numeroComanda}`)
      .pipe(
        first()
      );
  }

  public obterContaPagamentoComanda(numeroComanda: number) {
    return this.http.get<ApiResponse<ContaPagamentoComandaResponse>>(`${this.base}/pagamento/comanda?numeroComanda=${numeroComanda}`)
      .pipe(
        first()
      );
  }

  public imprimirPedido(numeroComanda: number) {
    return this.http.post<ApiResponse<number>>(`${this.base}/imprimirPedido?numeroComanda=${numeroComanda}`, null)
      .pipe(
        first()
      );
  }

  public obterListaDets(numeroComanda: number) {
    return this.http.get<ApiResponse<VendasDetalheModel[]>>(`${this.base}/listaDets?numeroComanda=${numeroComanda}`)
      .pipe(
        first()
      );
  }

  public obterMesa(numeroComanda: number) {
    return this.http.get<ApiResponse<Mesa>>(`${this.base}/obterMesa?numeroComanda=${numeroComanda}`)
      .pipe(
        first()
      );
  }

  public obterMesasDisponiveis() {
    return this.http.get<ApiResponse<MesasDisponiveisModel[]>>(`${this.base}/mesasDisponiveis`)
      .pipe(
        first()
      );
  }

  public obterMesasOcupadas() {
    return this.http.get<ApiResponse<MesasOcupadasModel[]>>(`${this.base}/mesasOcupadas`)
      .pipe(
        first()
      );
  }

  public obterPedidosBalcao() {
    return this.http.get<ApiResponse<PedidoBalcaoResponse[]>>(`${this.base}/pedidosBalcao`)
      .pipe(
        first()
      );
  }

  public obterPedidosDelivery() {
    return this.http.get<ApiResponse<PedidoDeliveryResponse[]>>(`${this.base}/pedidosDelivery`, { headers: { 'X-Skip-Loading-Interceptor': 'true' } })
      .pipe(
        first()
      );
  }

  public obterValorPago(numeroComanda: number) {
    return this.http.get<ApiResponse<number>>(`${this.base}/obterValorPago?numeroComanda=${numeroComanda}`)
      .pipe(
        first()
      );
  }

  public pagarConta(request: PagarContaRequest) {
    return this.http.post<ApiResponse<number>>(`${this.base}/pagarConta`, request)
      .pipe(
        first()
      );
  }

  public aplicarDesconto(request: PagarContaRequest) {
    return this.http.post<ApiResponse<number>>(`${this.base}/aplicarDesconto`, request)
    .pipe(
        first()
    );
  }

  public transferenciaMesa(request: TransferenciaMesaRequest) {
    return this.http.post<ApiResponse<number>>(`${this.base}/transferenciaMesa`, request)
      .pipe(
        first()
      );
  }

  public updateCodigoStatusDet(codigoDet: number, codigoStatus: number) {
    return this.http.post<ApiResponse<string>>(`${this.base}/atualizarCodigoStatusVendasDet?codigoDet=${codigoDet}&codigoStatus=${codigoStatus}`, {})
      .pipe(
        first()
      );
  }

  public verificarMesaCadastrada(numeroComanda: number) {
    return this.http.get<ApiResponse<boolean>>(`${this.base}/verificarMesaCadastrada?numeroComanda=${numeroComanda}`)
      .pipe(
        first()
      );
  }

  public verificarMesaOcupada(numeroComanda: number) {
    return this.http.get<ApiResponse<boolean>>(`${this.base}/verificarMesaOcupada?numeroComanda=${numeroComanda}`)
      .pipe(
        first()
      );
  }

  public getConfigPedido() {
    return this.http.get<ApiResponse<ConfigPdvDeliveryResponse>>(`${this.base}/integracao/config`).pipe(
      first(),
    );
  }

  public postConfigPedido(model: ConfigPedidoRequest) {
    return this.http.post<ApiResponse<ConfigPedidoRequest>>(`${this.base}/integracao/config`, model).pipe(
      first(),
    );
  }
}
